import React, { useState, useEffect, useContext } from "react";
import { Helmet } from "react-helmet";
import Jumbotron from "components/Jumbotron/Jumbotron";
import { useTranslation } from "react-i18next";
import "./SearchResult.scss";
import SearchBar from "features/search-property/components/Search-Bar/SearchBar";
import Filter from "./components/Filter/Filter";
import ToggleSwitch from "./components/Filter/components/ToggleSwitch";
import ResultCard from "./components/ResultCard/ResultCard";
import MobileSearch from "features/search-property/components/Search-Bar/components/MobileSearch/MobileSearch";
import MobileFilter from "./components/Filter/MobileFilter";
import { useAppDispatch, useAppSelector } from "store/store";
import { DirectionCheck } from "utils/classnameSwitch";
import {
  getSearchResult,
  searchresultSelector,
  getAmenities,
} from "./searchresult.slice";
import {
  searchSelector,
  activeFilters,
  getFeaturedStay,
} from "features/search-property/searchproperty.slice";
import SearchSkeleton from "features/search-property/components/SearchSkeleton/SearchSkeleton";
import SearchResultSkeleton from "features/search-property/components/SkeletonResults/SearchSkeleton";
import GMap from "components/GMap/GMap";
import InfiniteScroll from "react-infinite-scroll-component";
import {
  fetchAllProperties,
  fetchPropertiesData,
} from "./slices/fetchAllProperties.slice";
import DeepSearchBanner from "./components/DeepSearchBanner/DeepSearchBanner";
import ScrollToTop from "components/ScrollToTop/ScrollToTop";
import MapLoader from "./components/MapLoader/MapLoader";
import { Context } from "components/app/Wrapper";
import OfferCard from "features/offers/components/OfferCard/OfferCard";
import { IOffers } from "features/offers/offers.interface";
import { OFFER_MAPPING } from "features/offers/offers.config";

interface SearchResultProps {
  isOfferListing?: boolean;
  offerData?: IOffers | null;
}

const SearchResult: React.FC<SearchResultProps> = ({ isOfferListing = false, offerData = null }) => {
  const context: any = useContext(Context);
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const {
    searchResultList,
    isFetchingSearchResult,
    amenities,
    total_count,
    search_depth,
  } = useAppSelector(searchresultSelector);
  const {
    mapSearchResultList,
    isFetchingMapSearchResult,
    total_property_count,
  } = useAppSelector(fetchPropertiesData);
  const { filters, featuredstays, isFeaturedFetched } =
    useAppSelector(searchSelector);
  const [markerData, setMarkerData] = useState<any>([]);
  const [isMapView, setMapView] = useState(false);
  const [hasMore, updateHasMore] = useState(true);
  const [mapCardData, setMapCardData] = useState<any>({});
  const [dataAvailable, updateDataAvailableFlag] = useState(false);
  const [showNoResultBanner, setNoResultBanner] = useState(false);
  const [scroll, setScroll] = useState(false);

  const [dimensions, setDimensions] = useState(window.innerWidth);
  function handleResize() {
    setDimensions(window.innerWidth);
  }
  useEffect(() => {
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  });

  useEffect(() => {
    document.body.style.overflow = "visible";
    return () => {
      document.body.style.overflow = "hidden";
    };
  }, []);
  useEffect(() => {
    if (isOfferListing && !offerData) {
    return;
  }
    let obj = {...filters}
    obj.lang = context.locale;
    if(!obj.guests) {
      obj.guests = 2;
    }
    if (isOfferListing && offerData) {
      obj.offer_id = offerData.id;
    }
    dispatch(getSearchResult(obj));
  }, [dispatch, filters, context.locale, isOfferListing, offerData]);

  useEffect(() => {
    dispatch(getAmenities());
  }, [dispatch, context.locale]);

  useEffect(() => {
    setMapCardData({});
    if (isMapView) {
      if (isOfferListing && !offerData) {
        return;
      }
      const params = { ...filters, limit: 100 };
      if (isOfferListing && offerData) {
        params.offer_id = offerData.id;
      }
      dispatch(fetchAllProperties(params));
    }
  }, [isMapView, dispatch, filters, context.locale, isOfferListing, offerData]);

  useEffect(() => {
    window.addEventListener("scroll", () => {
      setScroll(window.scrollY > 200);
    });
  }, []);

  useEffect(() => {
    if (!isFetchingSearchResult) {
      setNoResultBanner(false);
      if (!filters.page || filters.page === 1) {
        updateDataAvailableFlag(true);
      } else {
        updateDataAvailableFlag(false);
      }
    } else {
      if (isFetchingSearchResult) {
        if (searchResultList.length > 0) {
          if (search_depth.date || search_depth.radius) {
            updateDataAvailableFlag(false);
            setNoResultBanner(true);
          } else {
            updateDataAvailableFlag(false);
            setNoResultBanner(false);
          }
        } else {
          updateDataAvailableFlag(true);
          setNoResultBanner(true);
        }
      }
    }
  }, [
    isFetchingSearchResult,
    searchResultList.length,
    search_depth,
    search_depth.radius,
    filters.page,
  ]);

  useEffect(() => {
    if (showNoResultBanner) {
      const list = isMapView ? mapSearchResultList : searchResultList;
      if (list.length === 0) {
        dispatch(getFeaturedStay());
      }
    }
  }, [
    showNoResultBanner,
    dispatch,
    mapSearchResultList,
    searchResultList,
    isMapView,
    context.locale
  ]);

  const markerSelected = (data: any) => {
    if (Object.keys(data).length) {
      const currentList = mapSearchResultList.length
        ? mapSearchResultList
        : featuredstays;
      const selectedMarkerData = currentList.filter(
        (item: any) => item.room_category.id === data.property_id
      );
      selectedMarkerData?.length && setMapCardData(selectedMarkerData[0]);
    } else {
      setMapCardData({});
    }
  };

  const toggleSwitchSelect = (checked: any) => {
    if (checked.selected === "map") {
      setMapView(true);
    } else {
      setMapView(false);
    }
  };
  const fetchMoreData = () => {
    if (total_count > 10) {
      const params = { ...filters };
      const currentPage = filters.page || 1;
      params["search_depth"] = search_depth;
      params["page"] = currentPage + 1;
      dispatch(activeFilters(params));
    }
  };

  useEffect(() => {
    const itemPerPage = 10;
    const totalResults = total_count;
    const currentPage = filters.page || 1;
    const totalPages = totalResults / itemPerPage;
    if (totalPages > currentPage && total_count !== 0) {
      updateHasMore(true);
    } else {
      updateHasMore(false);
    }
  }, [searchResultList, filters.page, total_count]);

  useEffect(() => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
    let markerList: any = [];
    const prepareMarkerData = () => {
      const currentArrayList = mapSearchResultList.length
        ? mapSearchResultList
        : featuredstays;
      currentArrayList &&
        currentArrayList.forEach((data: any) => formatMarkerOBj(data));
      setMarkerData(markerList);
    };
    const formatMarkerOBj = (item: any) => {
      const propertyObj = {
        ...item.property,
        name: item.room_category.name,
        rating_count: item.rating.count,
        room_category_rating: item.rating.score,
        price: item.price.member,
        property_id: item.room_category.id,
        image: item.images.length ? item.images[0].url : null,
        slug: item.room_category.slug,
        titleName: item.room_category.nice_name
      };
      markerList.push(propertyObj);
    };
    prepareMarkerData();
  }, [mapSearchResultList, featuredstays]);

  const showFeatured = () => {
    return (
      isFeaturedFetched && showNoResultBanner && searchResultList.length === 0
    );
  };

  const showTotalCount = () => {
    let list = showFeatured()
      ? featuredstays
      : isMapView
      ? mapSearchResultList
      : searchResultList;
    let total = showFeatured()
      ? featuredstays.length
      : isMapView
      ? total_property_count
      : total_count;
    let result = showFeatured()
      ? isFeaturedFetched
      : isMapView
      ? isFetchingMapSearchResult
      : isFetchingSearchResult;
    return (
      <div className={`${list && total > 0 && result ? "" : "hidden-vi"}`} dir={DirectionCheck()}>
        {t("searchResults.show")} {total}{" "}
        <span className="is-hidden-mobile">
        {t("searchResults.prop")}
          {filters.locations && !showFeatured() && (
            <> {t("searchResults.near")} {filters.locations}</>
          )}
        </span>
      </div>
    );
  };

  const renderListMap = () => {
    if (isMapView) {
      return (
        <>
          {!isFetchingMapSearchResult ? (
            <MapLoader />
          ) : (
            <>
              {showNoResultBanner && (
                <DeepSearchBanner
                  search_depth={search_depth.date}
                  isfeatured={showFeatured()}
                  locations={filters.locations}
                ></DeepSearchBanner>
              )}
              {showNoResultBanner && (
                <div
                  className={`search-subtitle search-result-subtitle 
                    ${showFeatured() ? "bullet-btn" : ""}`}
                >
                  {showTotalCount()}
                  <ToggleSwitch
                    onSelect={toggleSwitchSelect}
                    selectedValue={isMapView}
                  ></ToggleSwitch>
                </div>
              )}
              <div
                className={`map-height ${
                  markerData.length === 0 || showNoResultBanner ? "mt-60" : ""
                }`}
              >
                <GMap
                  data={markerData}
                  selectedMarker={markerSelected}
                  type="search-result"
                />
              </div>
              <div
                id="map-result"
                className={`${
                  Object.keys(mapCardData).length === 0
                    ? "hidecard"
                    : "showcard fadeInRight"
                } `}
              >
                <ResultCard {...mapCardData} filter={filters}></ResultCard>
              </div>
            </>
          )}
        </>
      );
    } else {
      return (
        <>
          {showNoResultBanner && (
            <DeepSearchBanner
              search_depth={search_depth.date}
              isfeatured={showFeatured()}
              locations={filters.locations}
            ></DeepSearchBanner>
          )}
          {showNoResultBanner && (
            <div
              className={`search-subtitle search-result-subtitle 
                    ${showFeatured() ? "bullet-btn" : ""}`}
            >
              {showTotalCount()}
              <ToggleSwitch
                onSelect={toggleSwitchSelect}
                selectedValue={isMapView}
              ></ToggleSwitch>
            </div>
          )}
          {!isFetchingSearchResult && (!filters.page || filters.page === 1) ? (
            <div
              className={`${
                dataAvailable || showNoResultBanner ? "mt-60" : ""
              }`}
            >
              <SearchResultSkeleton img={true}></SearchResultSkeleton>
            </div>
          ) : (
            <InfiniteScroll
              dataLength={searchResultList.length}
              next={fetchMoreData}
              hasMore={hasMore}
              endMessage={<p style={{ textAlign: "center" }}></p>}
              loader={<SearchSkeleton></SearchSkeleton>}
            >
              {searchResultList?.map((item: any, index: number) => (
                <ResultCard
                  {...item}
                  index={index}
                  filter={filters}
                  key={index}
                ></ResultCard>
              ))}
            </InfiniteScroll>
          )}

          {showFeatured() &&
            featuredstays?.map((item: any, index: number) => (
              <ResultCard
                {...item}
                index={index}
                filter={filters}
                key={index}
              ></ResultCard>
            ))}
        </>
      );
    }
  };

  const showToggleButton = !dataAvailable && !showNoResultBanner;

  return (
    <>
      <Helmet>
        <title>
          {isOfferListing && offerData
            ? `${t(OFFER_MAPPING[offerData?.type]).slice(0,-1)} - ${offerData.title}`
            : t("searchResults.pageTitle", "Search Properties - Find Your Perfect Stay")}
        </title>
      </Helmet>
      <div className="container-main search-result search" dir={DirectionCheck()}>
        <div className="search-container">
          {/* search bar */}
          <div className={`search-bar-content ${scroll ? "" : ""}`}>
            <Jumbotron
              title=""
              classes="search-header is-hidden-mobile show-ab-1100"
            >
              {" "}
            </Jumbotron>
            {dimensions >= 1100 && <div className="is-hidden-mobile show-ab-1100"><SearchBar></SearchBar></div>}
            {dimensions < 1100 && <div className="show-bl-1100"><MobileSearch></MobileSearch></div>}
            {showToggleButton && (
              <div className="search-subtitle search-result-subtitle" >
                {showTotalCount()}
                <ToggleSwitch
                  onSelect={toggleSwitchSelect}
                  selectedValue={isMapView}
                ></ToggleSwitch>
              </div>
            )}
          </div>
          {/* filters */}
          <div
            className={`columns search-result-container ${
              scroll ? "mt50c" : ""
            }`}
          >
            <div
              className={`column search-result-filter show-ab-1100
              ${
                dataAvailable || showNoResultBanner ? "mt-60" : ""
              }`}
            >
              <Filter amenities={amenities}></Filter>
            </div>
            {/* map & card */}
            <div
              className={`column renderlist-adjust ${!showToggleButton && isOfferListing ? "mt-60" : ""}`}
              style={{ padding: "0px" }}
            >
              {isOfferListing && offerData && (
                <div className="offer-listing-card-wrapper">
                  <OfferCard data={offerData} variant="offer-listing" isOfferListing={true} />
                </div>
              )}
              {renderListMap()}
            </div>
          </div>
        </div>
        <div className="show-bl-1100">
          <MobileFilter amenities={amenities}></MobileFilter>
        </div>
        <div style={{ height: "20vh" }}></div>
      </div>
      <ScrollToTop refID="" />
    </>
  );
};

export default SearchResult;
